/**
 * Object validators - Reusable complex object validators for schema and query returns
 *
 * These validators define the shape of nested objects used across the application.
 * Import from '../lib/validators' (via index.ts re-exports).
 */
import { Infer, v } from 'convex/values';
import { serviceTypeValidator } from './duty';
import { searchModeValidator } from './user';

// ============================================================================
// Location Validators
// ============================================================================

/**
 * Location for duties/services
 * Used in: duties table, query responses
 */
export const dutyLocationValidator = v.object({
  address: v.string(),
  city: v.string(),
  state: v.optional(v.string()),
  coordinates: v.array(v.number()), // [lat, lng]
});

export type DutyLocation = Infer<typeof dutyLocationValidator>;

/**
 * Location for user profiles (more fields than duty location)
 * Used in: userProfiles table, query responses
 */
export const profileLocationValidator = v.object({
  formattedAddress: v.optional(v.string()), // Full address for distance calculation
  street: v.optional(v.string()),
  postalCode: v.optional(v.string()),
  city: v.string(),
  state: v.optional(v.string()),
  coordinates: v.array(v.number()), // [lat, lng]
});

export type ProfileLocation = Infer<typeof profileLocationValidator>;

/**
 * Contract data location (simplified)
 */
export const contractLocationValidator = v.object({
  address: v.string(),
  city: v.string(),
});

export type ContractLocation = Infer<typeof contractLocationValidator>;

// ============================================================================
// Address Validators
// ============================================================================

/**
 * User address (on users table)
 * Used in: users table, query responses
 */
export const userAddressValidator = v.object({
  street: v.optional(v.string()),
  postalCode: v.optional(v.string()),
  city: v.optional(v.string()),
  country: v.optional(v.string()),
  state: v.optional(v.string()),
});

export type UserAddress = Infer<typeof userAddressValidator>;

/**
 * Billing address (simplified, no state)
 * Used in: billingInfos table
 */
export const billingAddressValidator = v.object({
  street: v.optional(v.string()),
  postalCode: v.optional(v.string()),
  city: v.optional(v.string()),
  country: v.optional(v.string()),
});

export type BillingAddress = Infer<typeof billingAddressValidator>;

/**
 * Practice address for business locations
 * Used in: businessLocations table
 */
export const practiceAddressValidator = v.object({
  invoiceName: v.optional(v.string()),
  invoiceNameLine2: v.optional(v.string()),
  bsnr: v.optional(v.string()),
  street: v.string(),
  postalCode: v.string(),
  city: v.string(),
  coordinates: v.optional(v.array(v.number())),
});

export type PracticeAddress = Infer<typeof practiceAddressValidator>;

/**
 * Billing address for business locations (with required fields)
 * Used in: businessLocations.billingAddress
 */
export const businessBillingAddressValidator = v.object({
  invoiceName: v.optional(v.string()),
  invoiceNameLine2: v.optional(v.string()),
  street: v.string(),
  postalCode: v.string(),
  city: v.string(),
});

export type BusinessBillingAddress = Infer<typeof businessBillingAddressValidator>;

// ============================================================================
// Profile Nested Object Validators
// ============================================================================

/**
 * Notify outside areas preference
 * Used in: userProfiles.notifyOutsideAreas
 */
export const notifyOutsideAreasValidator = v.object({
  enabled: v.boolean(), // Default: true
  minCompensation: v.number(), // Default: 90 (euros)
});

export type NotifyOutsideAreas = Infer<typeof notifyOutsideAreasValidator>;

/**
 * Availability flags for professional profile
 * Used in: userProfiles.professionalProfile.availabilityFlags
 */
export const availabilityFlagsValidator = v.object({
  weekends: v.boolean(),
  nights: v.boolean(),
  holidays: v.boolean(),
});

export type AvailabilityFlags = Infer<typeof availabilityFlagsValidator>;

/**
 * Professional profile (substitute only)
 * Used in: userProfiles.professionalProfile
 */
export const professionalProfileValidator = v.object({
  yearsInPractice: v.optional(v.string()), // '0-1', '1-3', '3-5', '5-10', '10-20', '20+'
  specialtyCertifications: v.optional(v.string()), // Comma-separated
  weeklyAvailability: v.optional(v.string()), // 'part-time', 'moderate', 'full-time', 'flexible'
  minHourlyRate: v.optional(v.number()), // Minimum hourly compensation (private)
  availabilityFlags: v.optional(availabilityFlagsValidator),
  emergencyContact: v.optional(v.string()), // Phone number (private)
  professionalReferences: v.optional(v.string()), // Free-text field
});

export type ProfessionalProfile = Infer<typeof professionalProfileValidator>;

/**
 * Service area preferences (substitute only)
 * Used in: userProfiles.serviceAreaPreferences
 */
export const serviceAreaPreferencesValidator = v.object({
  searchMode: searchModeValidator,
  // KV region selection (when searchMode === 'kv')
  selectedKvRegions: v.optional(v.array(v.string())), // e.g., ['KV Bayern', 'KV Berlin']
  // Distance-based selection (when searchMode === 'distance')
  distanceRadiusKm: v.optional(v.number()), // 0-300 km radius from user's home location
  // Service type filters
  preferredServiceTypes: v.array(serviceTypeValidator),
  // High compensation override
  enableHighCompOverride: v.boolean(),
  minCompensationOverride: v.optional(v.number()), // Hourly rate in euros
});

export type ServiceAreaPreferences = Infer<typeof serviceAreaPreferencesValidator>;

// ============================================================================
// Duty/Service Nested Object Validators
// ============================================================================

/**
 * Activity report (DA-383)
 * Used in: duties.activityReport
 */
export const activityReportValidator = v.object({
  kassenpatienten: v.number(),
  privatpatienten: v.number(),
  telefonischeBeratungen: v.number(),
  totenscheine: v.number(),
  submittedAt: v.number(),
  submittedBy: v.id('users'),
});

export type ActivityReport = Infer<typeof activityReportValidator>;

/**
 * Contract data validator
 */
export const contractDataValidator = v.object({
  dutyTitle: v.string(),
  startDate: v.number(),
  endDate: v.number(),
  location: contractLocationValidator,
  serviceType: v.string(),
  compensationType: v.string(),
  hourlyRate: v.optional(v.number()),
  minPrice: v.optional(v.number()),
  maxPrice: v.optional(v.number()),
  hasNotfallscheinbeteiligung: v.boolean(),
  notfallscheinbeteiligungRate: v.optional(v.number()),
});

export type ContractData = Infer<typeof contractDataValidator>;

// ============================================================================
// Business Location Nested Object Validators
// ============================================================================

/**
 * Doctor entry for business locations
 * Used in: businessLocations.doctors
 */
export const doctorEntryValidator = v.object({
  fullName: v.string(),
  lanr: v.optional(v.string()),
  kvSitz: v.optional(v.string()),
});

export type DoctorEntry = Infer<typeof doctorEntryValidator>;

/** Informational administrative contact; no login, notification or physician role. */
export const businessLocationContactValidator = v.object({
  name: v.string(),
  role: v.optional(v.string()),
  email: v.optional(v.string()),
  phone: v.optional(v.string()),
});

export type BusinessLocationContact = Infer<typeof businessLocationContactValidator>;

// ============================================================================
// Audit Target Validator
// ============================================================================

/**
 * Audit target object
 * Used in: audits.target
 */
export const auditTargetValidator = v.object({
  type: v.string(),
  id: v.string(),
});

export type AuditTarget = Infer<typeof auditTargetValidator>;
